// masOpciones.js — P3-12. «Más opciones» de WooCommerce por TIPO: qué campos activa cada tipo,
// cómo se leen de un producto de Woo al abrir la ficha, y cómo viajan al guardar (SOLO los tocados,
// SOLO los que el tipo activó). Frontend-only (export ES). Puro y testeable.
//
// Fase A (censo prod): SH no usa casi nada hoy → todos los flags nacen APAGADOS; un tipo sin
// `masOpciones` (config vieja) se comporta como antes. `shipping_class`/`upsell_ids` NO se exponen
// ni viajan (se preservan solos por no estar en la lista blanca de escritura).

import { bogotaLocalAGmtIso, gmtAInputBogota } from './colTimeUtils';

export const VISIBILIDADES = [
  { id: 'visible', label: 'Visible en todas partes' },
  { id: 'catalog', label: 'Solo en el catálogo' },
  { id: 'search', label: 'Solo en la búsqueda' },
  { id: 'hidden', label: 'Oculta' },
];
export const RESERVAS = [
  { id: 'no', label: 'No' },
  { id: 'yes', label: 'Permitir' },
  { id: 'notify', label: 'Permitir y avisar' },
];

// Los flags de un tipo (default TODO apagado). `ordenEjes` = slugs de eje en orden; [] = inactivo.
export function masOpcionesDeTipo(tipo) {
  const m = (tipo && tipo.masOpciones) || {};
  const umbral = m.umbralStockBajo;
  return {
    reservas: !!m.reservas,
    visibilidad: !!m.visibilidad,
    destacado: !!m.destacado,
    oferta: !!m.oferta,
    umbral: !!m.umbral, // ¿el tipo usa umbral de stock bajo?
    umbralDefault: (umbral === '' || umbral == null) ? null : (Number(umbral) >= 0 ? Number(umbral) : null),
    ordenEjes: Array.isArray(m.ordenEjes) ? m.ordenEjes.filter(Boolean) : [],
  };
}

// ¿El tipo activó ALGÚN campo gateado? (para decidir si el bloque muestra la sección de Woo).
export function tipoActivaAlgo(tipo) {
  const m = masOpcionesDeTipo(tipo);
  return m.reservas || m.visibilidad || m.destacado || m.oferta || m.umbral || m.ordenEjes.length > 0;
}

// Lee un producto (proyección /full) → los campos de «Más opciones» del formulario. Las fechas de
// oferta se muestran en hora Bogotá (desde el *_gmt). En variable, la oferta se deduce de las
// variaciones: `ofertaMixta` avisa si NO todas las rebajadas comparten las mismas fechas.
export function masOpcionesDesdeFull(full = {}) {
  const esVariable = (full.type || 'simple') === 'variable';
  const base = {
    featured: full.featured === true,
    catalogVisibility: full.catalog_visibility || 'visible',
    lowStockAmount: (full.low_stock_amount === '' || full.low_stock_amount == null) ? '' : String(full.low_stock_amount),
    slug: full.slug || '',
    defaultAttributes: Array.isArray(full.default_attributes) ? full.default_attributes : [],
    backorders: full.backorders || 'no', // reservas del producto SIMPLE (variable = por variación)
    ofertaDesde: '',
    ofertaHasta: '',
    ofertaMixta: false,
  };
  if (!esVariable) {
    base.ofertaDesde = gmtAInputBogota(full.date_on_sale_from_gmt || '');
    base.ofertaHasta = gmtAInputBogota(full.date_on_sale_to_gmt || '');
    return base;
  }
  // Variable: oferta = la de las variaciones CON precio rebajado (sale_price no vacío).
  const rebajadas = (full.variations || []).filter((v) => String(v.sale_price || '').trim() !== '');
  const pares = rebajadas.map((v) => `${v.date_on_sale_from_gmt || ''}|${v.date_on_sale_to_gmt || ''}`);
  const unicos = [...new Set(pares)];
  if (rebajadas.length && unicos.length === 1) {
    base.ofertaDesde = gmtAInputBogota(rebajadas[0].date_on_sale_from_gmt || '');
    base.ofertaHasta = gmtAInputBogota(rebajadas[0].date_on_sale_to_gmt || '');
  } else if (unicos.length > 1) {
    base.ofertaMixta = true; // la ficha avisa «algunas variaciones tienen fechas distintas»
  }
  return base;
}

// Payload de PRODUCTO: SOLO lo que el tipo activó Y el usuario tocó (`enviar(k)`). Slug viaja si
// se tocó (no está gateado por tipo). La oferta de producto SIMPLE va acá; la variable va por
// variación (payloadOfertaVariacion). Devuelve un objeto para fundir con el payload base.
export function payloadMasOpcionesProducto(form = {}, { enviar = () => true, tipo = null, esVariable = false } = {}) {
  const m = masOpcionesDeTipo(tipo);
  const out = {};
  if (m.destacado && enviar('featured')) out.featured = !!form.featured;
  if (m.visibilidad && enviar('catalogVisibility')) out.catalog_visibility = VISIBILIDADES.some((x) => x.id === form.catalogVisibility) ? form.catalogVisibility : 'visible';
  if (m.umbral && enviar('lowStockAmount')) out.low_stock_amount = String(form.lowStockAmount || '').trim() === '' ? null : parseInt(form.lowStockAmount, 10) || 0;
  if (enviar('slug') && form.slug != null) out.slug = String(form.slug).trim();
  // default_attributes viaja solo si el form lo trae (tocado) — nunca un [] «fantasma» en cada alta.
  if (enviar('defaultAttributes') && Array.isArray(form.defaultAttributes)) out.default_attributes = form.defaultAttributes;
  if (m.oferta && !esVariable && enviar('oferta')) {
    out.date_on_sale_from_gmt = bogotaLocalAGmtIso(form.ofertaDesde) || '';
    out.date_on_sale_to_gmt = bogotaLocalAGmtIso(form.ofertaHasta) || '';
  }
  // Reservas del producto SIMPLE (el variable las lleva por variación). notify/yes exige manage_stock
  // (Woo lo ignora sin él) — se envía junto, como en la variación.
  if (m.reservas && !esVariable && enviar('backorders')) {
    out.backorders = RESERVAS.some((x) => x.id === form.backorders) ? form.backorders : 'no';
    if (out.backorders !== 'no') out.manage_stock = true;
  }
  return out;
}

// Oferta por VARIACIÓN (variable): las fechas del producto aplican a TODA variación con precio
// rebajado. Devuelve el parche {date_on_sale_*_gmt} para una variación, o null si la variación NO
// está rebajada (no se le pone fecha). `enviar` gate por tocado-de-oferta a nivel ficha.
export function payloadOfertaVariacion(variacion = {}, form = {}, { tipo = null } = {}) {
  const m = masOpcionesDeTipo(tipo);
  if (!m.oferta) return null;
  const rebajada = String(variacion.sale_price || variacion.salePrice || '').trim() !== '';
  if (!rebajada) return { date_on_sale_from_gmt: '', date_on_sale_to_gmt: '' }; // sin rebaja → sin fechas (limpia)
  return {
    date_on_sale_from_gmt: bogotaLocalAGmtIso(form.ofertaDesde) || '',
    date_on_sale_to_gmt: bogotaLocalAGmtIso(form.ofertaHasta) || '',
  };
}

// Orden de los ejes (P3-7) aplicado a attributes[].position: toma el orden de slugs del TIPO y
// asigna position 0,1,2… a los attributes del producto en ese orden; los que no estén en el orden
// van después, conservando su orden relativo. Devuelve una COPIA de attributes con `position`.
// `slugDeAttr(attr)` resuelve el slug de eje de un attribute (los ejes vienen de /api/ejes).
export function aplicarOrdenEjes(attributes = [], ordenSlugs = [], slugDeAttr = () => '') {
  if (!Array.isArray(attributes) || !attributes.length) return attributes;
  const orden = Array.isArray(ordenSlugs) ? ordenSlugs : [];
  const rank = (attr) => {
    const i = orden.indexOf(slugDeAttr(attr));
    return i === -1 ? orden.length + 1 : i;
  };
  const conIdx = attributes.map((a, i) => ({ a, i }));
  conIdx.sort((x, y) => (rank(x.a) - rank(y.a)) || (x.i - y.i));
  return conIdx.map(({ a }, pos) => ({ ...a, position: pos }));
}

// ¿Aplica el orden de ejes este tipo? (hay orden configurado con ≥2 ejes).
export function tipoOrdenaEjes(tipo) {
  return masOpcionesDeTipo(tipo).ordenEjes.length >= 2;
}
